import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { basicURL } from "apiManager/APIConfig";
import {
  GetVerifiedActivityPostsCall,
  GetVerifiedActivityUsersCall,
} from "../../redux/api/question.api";

const USERS_PAGE_SIZE = 10;
const POSTS_PAGE_SIZE = 10;

function getCurrentMonthValue() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}`;
}

function toAbsoluteUrl(raw) {
  const input = String(raw || "").trim();
  if (!input) return "";
  if (/^https?:\/\//i.test(input)) return input;
  const base = String(basicURL || "").replace(/\/+$/, "");
  const path = input.replace(/^\/+/, "");
  return `${base}/${path}`;
}

function isVideoFile(file) {
  const mime = String(file?.type || "").toLowerCase();
  const blob = `${file?.url || ""} ${file?.name || ""}`.toLowerCase();
  return (
    mime.startsWith("video/") ||
    /\.(mp4|mov|m4v|webm)(\?|$|#)/i.test(blob)
  );
}

function getPostMedia(post) {
  const files = Array.isArray(post?.files) ? post.files : [];
  const video = files.find(isVideoFile);
  if (video?.url) {
    return { url: toAbsoluteUrl(video.url), isVideo: true };
  }
  const image =
    post?.thumbnail_url ||
    files.find((f) => f?.url && !isVideoFile(f))?.url ||
    "";
  if (!image) return { url: "", isVideo: false };
  return { url: toAbsoluteUrl(image), isVideo: false };
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function userDisplayName(user) {
  const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return full || user?.email || "User";
}

export default function VerifiedActivityPage() {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const postsPageSize = POSTS_PAGE_SIZE;
  const postGridColumns = isLgUp ? 3 : isMdUp ? 2 : 1;

  const [month, setMonth] = useState(getCurrentMonthValue);
  const [userSearch, setUserSearch] = useState("");
  const [postSearch, setPostSearch] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const [postsPage, setPostsPage] = useState(1);

  const [usersLoading, setUsersLoading] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);

  const usersPageCount = Math.max(1, Math.ceil(Number(usersTotal || 0) / USERS_PAGE_SIZE));
  const postsPageCount = Math.max(1, Math.ceil(Number(postsTotal || 0) / postsPageSize));

  useEffect(() => {
    setUsersPage(1);
  }, [userSearch, month]);

  useEffect(() => {
    setPostsPage(1);
  }, [selectedUserId, postSearch, month, postsPageSize]);

  useEffect(() => {
    let mounted = true;
    const loadUsers = async () => {
      setUsersLoading(true);
      const response = await GetVerifiedActivityUsersCall({
        month,
        search: userSearch.trim(),
        page: usersPage,
        limit: USERS_PAGE_SIZE,
      });
      if (!mounted) return;
      const incoming = Array.isArray(response?.users) ? response.users : [];
      setUsers(incoming);
      setUsersTotal(Number(response?.total || 0));

      if (!incoming.length) {
        setSelectedUserId("");
        setSelectedUser(null);
        setUsersLoading(false);
        return;
      }

      const stillVisible = incoming.find((u) => String(u.id) === String(selectedUserId));
      const next = stillVisible || incoming[0];
      setSelectedUserId(next.id);
      setSelectedUser(next);
      setUsersLoading(false);
    };

    loadUsers();
    return () => {
      mounted = false;
    };
  }, [month, userSearch, usersPage]);

  useEffect(() => {
    let mounted = true;
    const loadPosts = async () => {
      if (!selectedUserId) {
        setPosts([]);
        setPostsTotal(0);
        setSelectedUser(null);
        return;
      }
      setPostsLoading(true);
      const response = await GetVerifiedActivityPostsCall({
        user_id: selectedUserId,
        month,
        search: postSearch.trim(),
        page: postsPage,
        limit: postsPageSize,
      });
      if (!mounted) return;
      setPosts(Array.isArray(response?.posts) ? response.posts : []);
      setPostsTotal(Number(response?.total || 0));
      setSelectedUser(response?.user || null);
      setPostsLoading(false);
    };

    loadPosts();
    return () => {
      mounted = false;
    };
  }, [selectedUserId, month, postSearch, postsPage, postsPageSize]);

  const selectedUserLabel = useMemo(
    () => userDisplayName(selectedUser),
    [selectedUser]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={2} pb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3.6}>
            <Card sx={{ borderRadius: 3, p: 2, height: "100%" }}>
              <Stack spacing={1.2} mb={1.5}>
                <Typography variant="h5" fontWeight={700}>
                  Verified Users
                </Typography>
                <TextField
                  size="small"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  type="month"
                  fullWidth
                  label="Month"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search verified users"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              {usersLoading ? (
                <Box py={6} textAlign="center">
                  <CircularProgress size={28} />
                </Box>
              ) : users.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No verified users found.
                </Typography>
              ) : (
                <>
                  <List dense sx={{ maxHeight: "65vh", overflowY: "auto", p: 0 }}>
                    {users.map((u) => {
                      const selected = String(selectedUserId) === String(u.id);
                      return (
                        <ListItemButton
                          key={u.id}
                          selected={selected}
                          onClick={() => setSelectedUserId(u.id)}
                          sx={{ borderRadius: 1.5, mb: 0.5 }}
                        >
                          <Avatar
                            src={toAbsoluteUrl(u?.profile_image)}
                            sx={{ width: 34, height: 34, mr: 1.2 }}
                          >
                            {userDisplayName(u).charAt(0)}
                          </Avatar>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight={700} noWrap>
                                {userDisplayName(u)}
                              </Typography>
                            }
                            secondary={
                              <Stack direction="row" spacing={0.6} alignItems="center">
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {u?.email || ""}
                                </Typography>
                                <Chip
                                  size="small"
                                  color="success"
                                  label={`${u?.monthly_shared_count || 0} this month`}
                                />
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                  {usersTotal > USERS_PAGE_SIZE ? (
                    <Stack direction="row" justifyContent="flex-end" mt={1.2}>
                      <Pagination
                        page={usersPage}
                        count={usersPageCount}
                        color="primary"
                        shape="rounded"
                        onChange={(_, value) => setUsersPage(value)}
                      />
                    </Stack>
                  ) : null}
                </>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={8.4}>
            <Card sx={{ borderRadius: 3, p: 2, minHeight: 500 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                mb={1.5}
              >
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Activity Feed
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUserLabel
                      ? `${selectedUserLabel} - ${month}`
                      : "Select a verified user from the left"}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  value={postSearch}
                  onChange={(e) => setPostSearch(e.target.value)}
                  placeholder="Search selected user posts"
                  sx={{ minWidth: { xs: "100%", sm: 280 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {!selectedUserId ? (
                <Typography variant="body2" color="text.secondary">
                  Select a verified user to view community posts.
                </Typography>
              ) : postsLoading ? (
                <Box py={8} textAlign="center">
                  <CircularProgress size={28} />
                </Box>
              ) : posts.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No shared community posts for this user in {month}.
                </Typography>
              ) : (
                <>
                  <Grid container spacing={1.1}>
                    {posts.map((post) => {
                      const media = getPostMedia(post);
                      return (
                        <Grid item xs={12 / postGridColumns} key={post.id}>
                          <Card variant="outlined" sx={{ borderRadius: 2.2, p: 1.2, height: "100%" }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {post?.title || "Community post"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {formatDate(post?.created_at)}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 0.6,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {post?.body || "-"}
                            </Typography>
                            {media.url ? (
                              <Box mt={1}>
                                {media.isVideo ? (
                                  <Box
                                    component="video"
                                    controls
                                    preload="metadata"
                                    src={media.url}
                                    sx={{
                                      width: "100%",
                                      borderRadius: 1.2,
                                      border: "1px solid rgba(148,163,184,.35)",
                                      backgroundColor: "#000",
                                      maxHeight: 180,
                                    }}
                                  />
                                ) : (
                                  <Box
                                    component="img"
                                    src={media.url}
                                    alt="post media"
                                    sx={{
                                      width: "100%",
                                      borderRadius: 1.2,
                                      border: "1px solid rgba(148,163,184,.35)",
                                      objectFit: "cover",
                                      maxHeight: 180,
                                    }}
                                  />
                                )}
                              </Box>
                            ) : null}
                            <Stack direction="row" spacing={1.5} mt={1}>
                              <Stack direction="row" spacing={0.4} alignItems="center">
                                <ThumbUpOffAltIcon fontSize="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {post?.like_count || 0}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.4} alignItems="center">
                                <CommentOutlinedIcon fontSize="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {post?.comment_count || 0}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {postsTotal > postsPageSize ? (
                    <Stack direction="row" justifyContent="flex-end" mt={1.6}>
                      <Pagination
                        page={postsPage}
                        count={postsPageCount}
                        color="primary"
                        shape="rounded"
                        onChange={(_, value) => setPostsPage(value)}
                      />
                    </Stack>
                  ) : null}
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}
